import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { useDogProfileData } from "@/hooks/useDogProfileData";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import DonationFlow from "@/components/DonationFlow";
import PublicNav from "@/components/PublicNav";
import { useContent } from "@/context/ContentContext";
import { useT } from "@/lib/translations";
import { useLocalize } from "@/lib/localize";
import { useLanguage } from "@/context/LanguageContext";
import { trackFunnelStep } from "@/lib/analytics";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  PawPrint,
  Heart,
  Home,
  HandHeart,
  ChevronLeft,
  Calendar,
  Shield,
  Syringe,
  Sparkles,
  Share2,
  LogIn,
  Check,
  X,
  Upload,
  Utensils,
  Stethoscope,
  Image as ImageIcon,
  MessageCircle,
  ThumbsUp,
  Send,
  CheckCircle,
  User,
  Camera,
  Weight,
  Video,
  Twitter,
  Facebook,
  Instagram,
  Link as LinkIcon
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function DogProfilePage() {
  const { dogId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const supportIntent = searchParams.get("support");
  const { user, login } = useAuth();
  const fileInputRef = useRef(null);
  const { t } = useContent();
  const L = useT();
  const Lo = useLocalize();
  const { locale } = useLanguage();

  // 🔁 Tüm veri yüklemeleri custom hook üzerinden — async setState kuralından temiz
  const profileData = useDogProfileData(dogId);
  const {
    dog, healthRecords, events,
    photos, videos: dogVideos,
    comments, healthConfirmations,
    dogUpdates, dogUpdatesTotal,
    upcomingEvent,
    loading,
    refreshPhotos: fetchPhotos,
    refreshComments: fetchComments,
  } = profileData;
  // Not: guardian, guardians, guardianCount hook tarafından da fetch'leniyor
  // ama public sayfada GÖSTERİLMİYOR (Pativista'nın bütçe esnekliği için).
  // Admin panel ayrı endpoint kullanır.

  const [donationAmount, setDonationAmount] = useState(0);
  const [donating, setDonating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showMicroDonation, setShowMicroDonation] = useState(false);
  const [microDonationType, setMicroDonationType] = useState("meal");
  const [microQuantity, setMicroQuantity] = useState(1);
  const [showDonationFlow, setShowDonationFlow] = useState(false);
  const [donationFlowType, setDonationFlowType] = useState("genel");
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [newComment, setNewComment] = useState("");
  const [commentAuthorName, setCommentAuthorName] = useState("");
  const [isHealthConfirmation, setIsHealthConfirmation] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  // E-posta CTA: ?support=vet | ?support=meal → otomatik bağış modalını aç + scroll
  useEffect(() => {
    if (!dog || !supportIntent) return;
    const typeMap = { vet: "vet", meal: "mama" };
    const targetType = typeMap[supportIntent];
    if (!targetType) return;
    const tid = setTimeout(() => {
      setDonationFlowType(targetType);
      setShowDonationFlow(true);
      const el = document.getElementById("instant-support-card");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
    return () => clearTimeout(tid);
  }, [dog, supportIntent]);

  // Funnel: dog profile viewed — GA4 view_dog + Meta ViewContent (audience seed)
  useEffect(() => {
    if (!dog?.dog_id) return;
    trackFunnelStep("view_dog", {
      content_type: "dog",
      content_ids: [dog.dog_id],
      content_name: dog.name || "",
      dog_status: dog.status || "",
    });
  }, [dog?.dog_id]);

  const handleSubmitComment = async () => {
    if (!newComment.trim()) {
      toast.error(L("Lütfen bir yorum yazın"));
      return;
    }
    if (!commentAuthorName.trim()) {
      toast.error(L("Lütfen adınızı yazın"));
      return;
    }

    setSubmittingComment(true);
    try {
      await axios.post(`${API}/dogs/${dogId}/comments`, {
        dog_id: dogId,
        content: newComment,
        is_health_confirmation: isHealthConfirmation,
        author_name: commentAuthorName.trim(),
      }, { withCredentials: true });

      toast.success(isHealthConfirmation ? L("Sağlık onayınız eklendi!") : L("Yorumunuz eklendi!"));
      setNewComment("");
      setCommentAuthorName("");
      setIsHealthConfirmation(false);
      fetchComments();
    } catch (error) {
      toast.error(L("Yorum eklenemedi"));
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      await axios.post(`${API}/comments/${commentId}/like`);
      fetchComments();
    } catch (error) {
      console.error("Like error:", error);
    }
  };

  const handleDonate = async () => {
    if (!user) {
      login();
      return;
    }
    
    setDonating(true);
    try {
      await axios.post(`${API}/donations`, {
        dog_id: dogId,
        donor_name: user.name,
        donor_email: user.email,
        amount: donationAmount,
        message: `${dog.name} için bağış`,
        donation_type: "general"
      });
      toast.success(L("Bağışınız için teşekkür ederiz!"));
    } catch (error) {
      toast.error(L("Bağış işlemi başarısız"));
    } finally {
      setDonating(false);
    }
  };

  const handleMicroDonation = async () => {
    if (!user) {
      login();
      return;
    }
    
    setDonating(true);
    try {
      const response = await axios.post(`${API}/micro-donations`, {
        dog_id: dogId,
        donor_name: user.name,
        donor_email: user.email,
        donation_type: microDonationType,
        quantity: microQuantity
      });
      toast.success(response.data.message);
      setShowMicroDonation(false);
    } catch (error) {
      toast.error(L("Bağış işlemi başarısız"));
    } finally {
      setDonating(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(L("Lütfen bir resim dosyası seçin"));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(L("Dosya boyutu 5MB'dan küçük olmalı"));
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result.split(',')[1];
        
        await axios.post(`${API}/dogs/${dogId}/photos`, {
          dog_id: dogId,
          image_base64: base64,
          caption: `${dog.name} - ${new Date().toLocaleDateString(locale === "en" ? "en-US" : "tr-TR")}`
        });
        
        toast.success(L("Fotoğraf yüklendi!"));
        fetchPhotos();
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error(L("Fotoğraf yüklenemedi"));
    } finally {
      setUploading(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${dog.name} - KAHU`,
          text: `${dog.name}${L("'i tanıyın! ")}${Lo(dog, "story_short") || dog.story_short || L("Sahiplenmeyi bekliyor.")}`,
          url,
        });
      } catch (e) {
        if (e?.name !== "AbortError") console.warn("share failed", e);
      }
    }
  };

  const shareText = () =>
    `${dog?.name || L("Bu can")}${L("'i tanıyın! ")}${Lo(dog, "story_short") || dog?.story_short || L("Sahiplenmeyi bekliyor.")} ${window.location.href}`;

  // Backend OG-decorated URL — crawler'lar (Facebook, WhatsApp, iMessage, Twitter)
  // buradan zengin kart alır, insan ziyaretçiler otomatik /can/{id}'e redirect olur.
  const shareUrl = () => `${API}/share/dog/${dogId}`;

  const handleShareWhatsApp = () => {
    const wa = `https://wa.me/?text=${encodeURIComponent(`${dog?.name || L("Bu can")}${L("'i tanıyın! ")}` + shareUrl())}`;
    window.open(wa, "_blank", "noopener,noreferrer");
  };

  const handleShareTwitter = () => {
    const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${dog?.name || L("Bu can")}${L("'i tanıyın!")}`)}&url=${encodeURIComponent(shareUrl())}`;
    window.open(tw, "_blank", "noopener,noreferrer");
  };

  const handleShareFacebook = () => {
    // Facebook Sharer OG etiketlerini shareUrl()'den okur — köpek fotoğrafı + hikâyesi feed'e düşer
    const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl())}`;
    window.open(fb, "_blank", "noopener,noreferrer,width=580,height=520");
  };

  const handleShareInstagram = async () => {
    // Instagram'da direkt paylaş URL'i yok — link + metin panoya kopyalanıp Instagram açılıyor
    try {
      await navigator.clipboard.writeText(`${dog?.name || L("Bu can")}${L("'i tanıyın! ")}` + shareUrl());
      toast.success(L("Metin kopyalandı! Instagram DM veya story'de yapıştırabilirsin."), { duration: 4500 });
    } catch {
      try { window.prompt(L("Bu bağlantıyı kopyalayın ve Instagram'da paylaşın:"), shareUrl()); }
      catch { toast.error(L("Kopyalanamadı — bağlantıyı manuel paylaşın.")); }
    }
    setTimeout(() => {
      window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
    }, 700);
  };

  const downloadIgCard = async (format) => {
    // Backend'de Pillow ile üretilen 1080x1920 (story) veya 1080x1080 (post)
    // markalı JPEG'i indir. Toast ile kullanıcıyı yönlendir.
    const url = `${API}/share/instagram/${dogId}?format=${format}`;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `kahu-${dog?.name || dogId}-${format}.jpg`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(a.href);
        a.remove();
      }, 400);
      toast.success(
        format === "story"
          ? L("Story görseli indirildi. Instagram Story'e yükle, link sticker ekle → paylaş!")
          : L("Feed görseli indirildi. Instagram post olarak paylaş, açıklamaya bağlantıyı yapıştır."),
        { duration: 5500 }
      );
    } catch (e) {
      console.error("IG card download failed", e);
      toast.error(L("Görsel indirilemedi. Bu can için fotoğraf gerekli olabilir."));
    }
  };

  const handleCopyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(L("Bağlantı kopyalandı!"));
    } catch {
      try { window.prompt(L("Bu bağlantıyı kopyalayın:"), url); }
      catch { toast.error(L("Kopyalanamadı, adresi manuel kopyalayın.")); }
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      barinakta: L("Alanda"),
      sahiplendirmeye_hazir: L("Sahiplenmeye Hazır"),
      sahiplendirildi: L("Sahiplendirildi"),
      koruyucu_ailede: L("Geçici Bakımda"),
      tedavi_goruyor: L("Tedavi Görüyor"),
      transfer: L("Transferde"),
      beklemede: L("Beklemede"),
      kayip: L("Kayıp"),
      vefat: L("Vefat"),
    };
    return labels[status] || status;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString(locale === "en" ? "en-US" : "tr-TR");
  };

  const getMicroPrice = (type) => {
    return type === "meal" ? 25 : 150;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!dog) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <PawPrint className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h2 className="font-heading text-2xl mb-2">Can Bulunamadı</h2>
          <Link to="/canlar">
            <Button className="mt-4">Canları Keşfet</Button>
          </Link>
        </div>
      </div>
    );
  }

  const currentPhoto = photos[selectedPhotoIndex];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <PublicNav
        rightSlot={
          <>
            <span className="hidden md:inline text-sm text-muted-foreground italic" data-testid="share-hint-text">
              {L("Bu Can Profilini paylaş, sesimizi duyur!")}
            </span>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="share-btn" aria-label={L("Bu Can Profilini paylaş, sesimizi duyur!")} title={L("Bu Can Profilini paylaş, sesimizi duyur!")}>
                  <Share2 className="w-5 h-5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-1.5">
                <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground px-3 py-2">
                  {dog?.name} {L("paylaş")}
                </p>
                <button
                  onClick={handleShareWhatsApp}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-sm"
                  data-testid="share-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
                  <span>{L("WhatsApp'ta paylaş")}</span>
                </button>
                <button
                  onClick={handleShareTwitter}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-sm"
                  data-testid="share-twitter-btn"
                >
                  <Twitter className="w-4 h-4 text-sky-500" strokeWidth={1.5} />
                  <span>{L("X (Twitter) üzerinden")}</span>
                </button>
                <button
                  onClick={handleShareFacebook}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-sm"
                  data-testid="share-facebook-btn"
                >
                  <Facebook className="w-4 h-4 text-blue-600" strokeWidth={1.5} />
                  <span>{L("Facebook'ta paylaş")}</span>
                </button>
                <button
                  onClick={() => downloadIgCard("story")}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-sm"
                  data-testid="share-instagram-story-btn"
                >
                  <Instagram className="w-4 h-4 text-pink-600" strokeWidth={1.5} />
                  <div className="flex flex-col items-start leading-tight">
                    <span>{L("Instagram Story için indir")}</span>
                    <span className="text-[10px] text-muted-foreground">{L("Markalı 1080×1920 görsel")}</span>
                  </div>
                </button>
                <button
                  onClick={() => downloadIgCard("post")}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-sm"
                  data-testid="share-instagram-post-btn"
                >
                  <Instagram className="w-4 h-4 text-pink-600" strokeWidth={1.5} />
                  <div className="flex flex-col items-start leading-tight">
                    <span>{L("Instagram Feed için indir")}</span>
                    <span className="text-[10px] text-muted-foreground">{L("Markalı 1080×1080 görsel")}</span>
                  </div>
                </button>
                <button
                  onClick={handleShareInstagram}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-sm"
                  data-testid="share-instagram-btn"
                >
                  <Instagram className="w-4 h-4 text-pink-600" strokeWidth={1.5} />
                  <div className="flex flex-col items-start leading-tight">
                    <span>{L("Instagram DM'e link")}</span>
                    <span className="text-[10px] text-muted-foreground">{L("Link kopyala + IG aç")}</span>
                  </div>
                </button>
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-sm"
                  data-testid="share-copy-btn"
                >
                  <LinkIcon className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  <span>{L("Bağlantıyı kopyala")}</span>
                </button>
                {typeof navigator !== "undefined" && navigator.share && (
                  <button
                    onClick={handleShare}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-sm"
                    data-testid="share-native-btn"
                  >
                    <Share2 className="w-4 h-4 text-foreground/60" strokeWidth={1.5} />
                    <span>{L("Cihaz paylaşımı")}</span>
                  </button>
                )}
              </PopoverContent>
            </Popover>
          </>
        }
      />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 pb-24 lg:pb-8">
        {/* Breadcrumb */}
        <Link to="/canlar" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ChevronLeft className="w-4 h-4" />
          {L("Canlarımız")}
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8 min-w-0">
            {/* Photo Gallery */}
            <div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-primary/10">
                <img
                  src={currentPhoto?.media_base64 
                    || (currentPhoto?.image_base64 ? `data:image/jpeg;base64,${currentPhoto.image_base64}` : null)
                    || dog.photo_url 
                    || "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=800&h=600&fit=crop"
                  }
                  alt={dog.name}
                  className="w-full max-h-[560px] object-contain rounded-3xl"
                  style={{ background: "linear-gradient(135deg, #f5f0eb 0%, #ede6df 100%)" }}
                  data-testid="dog-main-photo"
                />
              </div>
              <div className="mt-4 px-1">
                <Badge className={`mb-2 text-sm px-3.5 py-1 ${
                  dog.status === "sahiplendirmeye_hazir" ? "status-available" :
                  dog.status === "koruyucu_ailede" ? "status-foster" :
                  dog.status === "tedavi_goruyor" ? "bg-amber-100 text-amber-800" :
                  dog.status === "transfer" ? "bg-blue-100 text-blue-800" :
                  dog.status === "beklemede" ? "bg-slate-100 text-slate-700" :
                  dog.status === "kayip" ? "bg-red-100 text-red-800" :
                  dog.status === "vefat" ? "bg-neutral-200 text-neutral-700" : "bg-muted"
                }`}>
                  {getStatusLabel(dog.status)}
                </Badge>
                <h1 className="font-heading text-4xl md:text-5xl" data-testid="dog-name">
                  {dog.name}
                </h1>
              </div>

              {/* Upcoming Event Card — only if this dog is featured */}
              {upcomingEvent && (
                <Link
                  to={`/etkinlikler/${upcomingEvent.event_id}`}
                  className="block mt-4 rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-rose-50 p-4 hover:shadow-lg transition-all"
                  data-testid="dog-upcoming-event"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs uppercase tracking-wider text-primary font-semibold">
                        {dog.name}, {upcomingEvent.location_name}&apos;ta seni bekliyor
                      </p>
                      <p className="font-medium text-foreground text-sm md:text-base">
                        {(() => {
                          try {
                            return new Date(upcomingEvent.event_date).toLocaleDateString("tr-TR", {
                              weekday: "long",
                              day: "numeric",
                              month: "long",
                            });
                          } catch {
                            return upcomingEvent.event_date;
                          }
                        })()}
                        {" · "}
                        {upcomingEvent.start_time}–{upcomingEvent.end_time}
                      </p>
                      {upcomingEvent.subtitle && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {upcomingEvent.title} · {upcomingEvent.subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              )}
              
              {/* Photo upload removed from public - admin only via panel */}
            </div>

            {/* Photo Thumbnails */}
            {photos.length > 0 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {photos.map((photo, idx) => (
                  <button
                    key={photo.photo_id}
                    onClick={() => setSelectedPhotoIndex(idx)}
                    className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden transition-all ${
                      idx === selectedPhotoIndex ? "ring-2 ring-primary ring-offset-2" : "opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img
                      src={photo.media_base64 || (photo.image_base64 ? `data:image/jpeg;base64,${photo.image_base64}` : "")}
                      alt={`${dog.name} ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Info Cards */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Card className="bg-card border border-border/50">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">KAHU ID</p>
                    <p className="font-medium">{dog.kahu_id || dog.dog_id?.replace("dog_", "KAHU-").toUpperCase()}</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border border-border/50">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{L("Geliş Tarihi")}</p>
                    <p className="font-medium">{formatDate(dog.intake_date)}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabs — mobile: horizontal scroll; md+: 5-col grid */}
            <Tabs defaultValue="about" className="w-full">
              <TabsList className="w-full h-auto bg-muted/50 p-1 rounded-xl flex flex-wrap justify-start gap-1 md:grid md:grid-cols-5 md:gap-0 md:flex-nowrap">
                <TabsTrigger value="about" className="rounded-lg shrink-0 whitespace-nowrap">{L("Hakkında")}</TabsTrigger>
                <TabsTrigger value="updates" className="rounded-lg shrink-0 whitespace-nowrap flex items-center gap-1">
                  <Camera className="w-3.5 h-3.5" />
                  {L("Güncellemeler")}
                  {dogUpdatesTotal > 0 && <span className="text-xs bg-primary/10 text-primary px-1.5 rounded-full">{dogUpdatesTotal}</span>}
                </TabsTrigger>
                <TabsTrigger value="comments" className="rounded-lg shrink-0 whitespace-nowrap flex items-center gap-1">
                  <MessageCircle className="w-4 h-4" />
                  <span>{L("Yorumlar")}</span>
                  {comments.length > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                      {comments.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="health" className="rounded-lg shrink-0 whitespace-nowrap">{L("Sağlık")}</TabsTrigger>
                <TabsTrigger value="timeline" className="rounded-lg shrink-0 whitespace-nowrap">{L("Zaman")}</TabsTrigger>
              </TabsList>

              <TabsContent value="about" className="mt-6 space-y-6">
                {/* Traits */}
                <Card className="bg-card border border-border/50">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl">{L("Özellikler")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <span className="text-muted-foreground">{L("Cinsiyet:")}</span>
                        <span className="font-medium">{dog.gender === "male" ? L("Erkek") : L("Dişi")}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-muted-foreground">{L("Boyut:")}</span>
                        <span className="font-medium">
                          {dog.size === "small" ? L("Küçük") : dog.size === "medium" ? L("Orta") : dog.size === "large" ? L("Büyük") : L("Dev")}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-muted-foreground">{L("Irk:")}</span>
                        <span className="font-medium">{Lo(dog, "breed") || L("Karışık")}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-muted-foreground">{L("Doğum:")}</span>
                        <span className="font-medium">{dog.approx_birth_date || L("Bilinmiyor")}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3 mt-6">
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${dog.is_child_friendly ? 'bg-accent/10 text-accent' : 'bg-muted text-muted-foreground'}`}>
                        {dog.is_child_friendly ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        {L("Çocuklarla Uyumlu")}
                      </div>
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${dog.is_dog_friendly ? 'bg-accent/10 text-accent' : 'bg-muted text-muted-foreground'}`}>
                        {dog.is_dog_friendly ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        {L("Köpeklerle Uyumlu")}
                      </div>
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${dog.is_apartment_suitable ? 'bg-accent/10 text-accent' : 'bg-muted text-muted-foreground'}`}>
                        {dog.is_apartment_suitable ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        {L("Apartmana Uygun")}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Story */}
                <Card className="bg-card border border-border/50">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl">{L("Hikayesi")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(Lo(dog, "story_long") || dog.story_long) ? (
                      <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                        {Lo(dog, "story_long") || dog.story_long}
                      </p>
                    ) : (Lo(dog, "story_short") || dog.story_short) ? (
                      <p className="text-muted-foreground leading-relaxed">
                        {Lo(dog, "story_short") || dog.story_short}
                      </p>
                    ) : (
                      <p className="text-muted-foreground italic">
                        {L("Bu can için henüz bir hikaye yazılmamış.")}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Updates Tab */}
              <TabsContent value="updates" className="mt-6">
                <Card className="bg-card border border-border/50">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Camera className="w-5 h-5 text-primary" />
                      {L("Son Güncellemeler")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dogUpdates.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">
                        {L("Henuz guncelleme paylasilmamis.")}
                      </p>
                    ) : (
                      <div className="space-y-4" data-testid="public-updates-list">
                        {dogUpdates.map((update) => (
                          <div key={update.update_id} className="rounded-xl overflow-hidden border border-border/50" data-testid={`public-update-${update.update_id}`}>
                            <img src={update.image_base64} alt={L("Güncelleme")} className="w-full max-h-72 object-cover" />
                            <div className="p-3">
                              {update.note && <p className="text-sm">{update.note}</p>}
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(update.created_at).toLocaleDateString(locale === "en" ? "en-US" : "tr-TR", { day: "numeric", month: "long", year: "numeric" })}
                              </p>
                            </div>
                          </div>
                        ))}
                        {dogUpdatesTotal > 3 && (
                          <p className="text-center text-sm text-muted-foreground">
                            {L("Toplam {n} guncelleme mevcut.").replace("{n}", dogUpdatesTotal)}
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Comments Tab */}
              <TabsContent value="comments" className="mt-6 space-y-6">
                {/* Health Confirmation Banner */}
                {healthConfirmations > 0 && (
                  <div className="flex items-center gap-3 p-4 bg-accent/10 rounded-xl border border-accent/20">
                    <CheckCircle className="w-6 h-6 text-accent" />
                    <div>
                      <p className="font-medium text-accent">{L("Sağlık Onayları")}</p>
                      <p className="text-sm text-muted-foreground">
                        {L("{n} hayvan sever bu canın sağlığını onayladı").replace("{n}", healthConfirmations)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Add Comment Form */}
                <Card className="bg-card border border-border/50">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <MessageCircle className="w-5 h-5 text-primary" />
                      {L("Yorum Yaz")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="comment-author-name" className="text-sm mb-1.5 block">
                        {L("Adınız")} <span className="text-primary">*</span>
                      </Label>
                      <input
                        id="comment-author-name"
                        type="text"
                        value={commentAuthorName}
                        onChange={(e) => setCommentAuthorName(e.target.value)}
                        placeholder={L("Örn. Ayşe Y.")}
                        maxLength={60}
                        className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                        data-testid="comment-author-name-input"
                      />
                    </div>
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder={L("{name} hakkında bir yorum yazın... Ziyaret ettiniz mi? Nasıl görünüyordu?").replace("{name}", dog.name)}
                      className="min-h-[100px] rounded-xl"
                      data-testid="comment-input"
                    />
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="health-confirm"
                        checked={isHealthConfirmation}
                        onCheckedChange={setIsHealthConfirmation}
                        data-testid="health-confirm-checkbox"
                      />
                      <Label htmlFor="health-confirm" className="flex items-center gap-2 cursor-pointer text-sm">
                        <CheckCircle className="w-4 h-4 text-accent" />
                        {L("Bu canın sağlığını onaylıyorum (yakın zamanda gördüm)")}
                      </Label>
                    </div>

                    <div className="flex justify-end items-center">
                      <Button
                        onClick={handleSubmitComment}
                        disabled={submittingComment || !newComment.trim() || !commentAuthorName.trim()}
                        className="btn-pill bg-primary text-primary-foreground"
                        data-testid="submit-comment-btn"
                      >
                        {submittingComment ? L("Gönderiliyor...") : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            {L("Gönder")}
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Comments List */}
                <Card className="bg-card border border-border/50">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">
                      {L("Yorumlar")} ({comments.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {comments.length === 0 ? (
                      <div className="text-center py-8">
                        <MessageCircle className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                        <p className="text-muted-foreground">{L("Henüz yorum yok")}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {L("Bu can hakkında ilk yorumu siz yapın!")}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {comments.map((comment) => (
                          <div 
                            key={comment.comment_id} 
                            className={`p-4 rounded-xl ${
                              comment.is_health_confirmation 
                                ? 'bg-accent/5 border border-accent/20' 
                                : 'bg-muted/30'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                                {comment.author_picture ? (
                                  <img 
                                    src={comment.author_picture} 
                                    alt={comment.author_name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <User className="w-5 h-5 text-muted-foreground" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium">{comment.author_name}</span>
                                  {comment.is_verified && (
                                    <Badge variant="secondary" className="text-xs">{L("Doğrulanmış")}</Badge>
                                  )}
                                  {comment.is_health_confirmation && (
                                    <Badge className="bg-accent/20 text-accent text-xs">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      {L("Sağlık Onayı")}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {comment.content}
                                </p>
                                <div className="flex items-center gap-4 mt-2">
                                  <button 
                                    onClick={() => handleLikeComment(comment.comment_id)}
                                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                                  >
                                    <ThumbsUp className="w-3 h-3" />
                                    {comment.likes > 0 && comment.likes}
                                  </button>
                                  <span className="text-xs text-muted-foreground">
                                    {formatDate(comment.created_at)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Videos */}
                {dogVideos.length > 0 && (
                  <Card className="bg-card border border-border/50" data-testid="dog-videos-section">
                    <CardHeader>
                      <CardTitle className="font-heading text-xl flex items-center gap-2">
                        <Video className="w-5 h-5 text-accent" />
                        {L("Videolar")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {dogVideos.map((video) => (
                        <div key={video.video_id} className="rounded-xl overflow-hidden">
                          {video.type === "youtube" ? (
                            <iframe
                              src={video.url.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/")}
                              className="w-full aspect-video rounded-xl"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              title="Video"
                            />
                          ) : (
                            <video
                              src={video.url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${video.url}` : video.url}
                              controls
                              className="w-full aspect-video rounded-xl bg-black"
                              preload="metadata"
                            />
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="health" className="mt-6 space-y-6">
                {/* Medical Info from Dog Profile */}
                {(dog.is_neutered || dog.vaccination_status || dog.parasite_treatment || dog.weight || dog.health_notes || (dog.vaccinations && dog.vaccinations.length > 0) || dog.vet_name || dog.vet_clinic) ? (
                  <Card className="bg-card border border-border/50">
                    <CardHeader>
                      <CardTitle className="font-heading text-xl flex items-center gap-2">
                        <Shield className="w-5 h-5 text-accent" />
                        {L("Sağlık Durumu")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-5">
                      {/* Status Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="health-status-grid">
                        {dog.is_neutered && (
                          <div className="p-3 bg-muted/30 rounded-xl text-center" data-testid="neutered-status">
                            <p className="text-xs text-muted-foreground mb-1">{L("Kısırlaştırma")}</p>
                            <p className="font-medium text-sm">
                              {dog.is_neutered === "evet" ? L("Kısırlaştırıldı") : dog.is_neutered === "hayir" ? L("Kısırlaştırılmadı") : L("Bilinmiyor")}
                            </p>
                          </div>
                        )}
                        {dog.vaccination_status && (
                          <div className="p-3 bg-muted/30 rounded-xl text-center" data-testid="vaccination-status">
                            <p className="text-xs text-muted-foreground mb-1">{L("Aşı Durumu")}</p>
                            <p className="font-medium text-sm">
                              {dog.vaccination_status === "tam" ? L("Tam") : dog.vaccination_status === "eksik" ? L("Eksik") : dog.vaccination_status === "yapilmadi" ? L("Yapılmadı") : L("Bilinmiyor")}
                            </p>
                          </div>
                        )}
                        {dog.parasite_treatment && (
                          <div className="p-3 bg-muted/30 rounded-xl text-center" data-testid="parasite-status">
                            <p className="text-xs text-muted-foreground mb-1">{L("Parazit Tedavisi")}</p>
                            <p className="font-medium text-sm">
                              {dog.parasite_treatment === "yapildi" ? L("Yapıldı") : dog.parasite_treatment === "yapilmadi" ? L("Yapılmadı") : L("Bilinmiyor")}
                            </p>
                          </div>
                        )}
                        {dog.weight && (
                          <div className="p-3 bg-muted/30 rounded-xl text-center" data-testid="weight-status">
                            <p className="text-xs text-muted-foreground mb-1">{L("Kilo")}</p>
                            <p className="font-medium text-sm flex items-center justify-center gap-1">
                              <Weight className="w-3.5 h-3.5" />
                              {dog.weight} {L("kg")}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Vaccination Details */}
                      {dog.vaccinations && dog.vaccinations.length > 0 && (
                        <div data-testid="vaccination-details">
                          <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                            <Syringe className="w-4 h-4 text-accent" />
                            Aşı Detayları
                          </h4>
                          <div className="space-y-1.5">
                            {dog.vaccinations.map((vac, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2.5 bg-muted/20 rounded-lg text-sm">
                                <span className="font-medium">{vac.name}</span>
                                {vac.date && (
                                  <span className="text-muted-foreground text-xs">{formatDate(vac.date)}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Health Notes */}
                      {dog.health_notes && (
                        <div data-testid="health-notes">
                          <h4 className="text-sm font-medium mb-2">{L("Sağlık Notları")}</h4>
                          <p className="text-sm text-muted-foreground bg-muted/20 p-3 rounded-lg whitespace-pre-wrap">{dog.health_notes}</p>
                        </div>
                      )}

                      {/* Vet Info */}
                      {(dog.vet_name || dog.vet_clinic) && (
                        <div className="flex items-start gap-3 p-3 bg-muted/20 rounded-lg" data-testid="vet-info">
                          <Stethoscope className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                          <div>
                            <h4 className="text-sm font-medium">{L("Veteriner")}</h4>
                            {dog.vet_name && <p className="text-sm text-muted-foreground">{dog.vet_name}</p>}
                            {dog.vet_clinic && <p className="text-xs text-muted-foreground">{dog.vet_clinic}</p>}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : null}

                {/* Existing Health Records */}
                <Card className="bg-card border border-border/50">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Syringe className="w-5 h-5 text-accent" />
                      {L("Sağlık Kayıtları")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {healthRecords.length === 0 && !(dog.is_neutered || dog.vaccination_status || dog.parasite_treatment || dog.weight || dog.health_notes || (dog.vaccinations && dog.vaccinations.length > 0)) ? (
                      <p className="text-muted-foreground text-center py-8">
                        {L("Henüz sağlık bilgisi girilmedi")}
                      </p>
                    ) : healthRecords.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4 text-sm">
                        {L("Ek sağlık kaydı bulunmuyor")}
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {healthRecords.map((record) => (
                          <div key={record.record_id} className="p-4 bg-muted/30 rounded-xl">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium">{record.record_type}</span>
                              <span className="text-sm text-muted-foreground">
                                {formatDate(record.created_at)}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">{record.description}</p>
                            {record.vet_name && (
                              <p className="text-xs text-muted-foreground mt-2">
                                {L("Veteriner:")} {record.vet_name}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="timeline" className="mt-6">
                <Card className="bg-card border border-border/50">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl">{L("Zaman Çizelgesi")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {events.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">
                        {L("Henüz olay kaydı yok")}
                      </p>
                    ) : (
                      <div className="relative pl-6 border-l-2 border-border space-y-6">
                        {events.map((event) => (
                          <div key={event.event_id} className="relative">
                            <div className="absolute -left-[25px] w-4 h-4 rounded-full bg-primary" />
                            <div>
                              <p className="font-medium">{event.event_type}</p>
                              <p className="text-sm text-muted-foreground">{event.notes}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatDate(event.created_at)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar - Action Cards */}
          <div className="space-y-6 min-w-0">
            {/* Adopt Card */}
            <Card className="bg-card border border-border/50 shadow-xl shadow-primary/10 lg:sticky lg:top-24 z-10">
              <CardContent className="p-7 space-y-5">
                <h3 className="font-heading text-2xl">
                  {dog.name}{L("'e ")}<span className="text-primary">{L(t("profile.adopt.title_suffix", "Yuva Ol"))}</span>
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {L(t("profile.adopt.description", "Bu güzel can seni bekliyor. Başvuru formunu doldur, hayatını değiştirecek dostunla tanış."))}
                </p>
                <Link to={`/sahiplen/${dogId}`} data-testid="adopt-btn" className="block pt-1">
                  <Button size="xl" className="w-full btn-pill btn-primary bg-primary text-primary-foreground shadow-lg shadow-primary/20 font-semibold transition-all duration-200 hover:bg-primary hover:text-white hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/30 whitespace-normal h-auto min-h-14 py-2.5 leading-tight">
                    <Heart className="w-5 h-5 mr-2 shrink-0" />
                    <span className="flex flex-col items-center leading-tight text-center min-w-0">
                      <span className="text-sm sm:text-base font-semibold">{L(t("profile.adopt.button", "Sahiplenmek İstiyorum"))}</span>
                      <span className="text-xs sm:text-sm opacity-80 mt-0.5">{L(t("profile.adopt.button.line2", "(Ücretsiz Başvuru)"))}</span>
                    </span>
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Guardian Family Card — 1:N model: bir cana birden fazla destekçi olabilir.
                Public görünümde destekçi sayısı paylaşılmıyor (Pativista'nın bütçe esnekliği için). */}
            <Card className="bg-card border border-border/50">
              <CardContent className="p-7 space-y-5">
                <h3 className="font-heading text-xl">{L(t("profile.guardian.title", "Koruyucu Aile Ol"))}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {L(t("profile.guardian.description", "Her ay düzenli destekle bakım masraflarına omuz ver. Sahiplenemiyorum diye üzülmek yerine, yaşam alanlarımızda güvenle bakmamıza destek ol."))}
                </p>
                <Link to={`/koruyucu-aile/${dogId}`} data-testid="guardian-btn" className="block pt-1">
                  <Button size="lg" variant="outline" className="w-full btn-pill border-2 border-secondary text-secondary whitespace-normal h-auto min-h-14 py-2.5 leading-tight">
                    <Home className="w-5 h-5 mr-2 shrink-0" />
                    <span className="flex flex-col items-center leading-tight text-center min-w-0">
                      <span className="text-sm sm:text-base font-semibold">{L(t("profile.guardian.button.line1", "Koruyucu Aile Ol"))}</span>
                      <span className="text-xs sm:text-sm opacity-80 mt-0.5">{L(t("profile.guardian.button.line2", "(Aylık Destek)"))}</span>
                    </span>
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Anında Destek */}
            <Card id="instant-support-card" className="bg-gradient-to-br from-accent/5 to-accent/10 border border-accent/20" data-testid="instant-support-card">
              <CardContent className="p-7 space-y-5">
                <h3 className="font-heading text-xl flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-accent" />
                  {L(t("profile.support.title", "Anında Destek"))}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {L(t("profile.support.description", "Küçük bir destek bile bugün fark yaratmaya yeter."))}
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => { setDonationFlowType("mama"); setShowDonationFlow(true); }}
                    className="p-5 bg-background rounded-xl border-2 border-secondary/60 text-secondary transition-all duration-200 text-left group hover:bg-accent hover:border-accent hover:text-accent-foreground hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30"
                    data-testid="meal-donation-btn"
                  >
                    <Utensils className="w-7 h-7 mb-3 transition-transform group-hover:scale-110" />
                    <p className="font-semibold text-base mb-1">{L(t("profile.mama.title", "Mama Desteği"))}</p>
                    <p className="text-sm opacity-80">{L(t("profile.mama.subtitle", "Karnı doysun"))}</p>
                  </button>
                  
                  <button
                    onClick={() => { setDonationFlowType("vet"); setShowDonationFlow(true); }}
                    className="p-5 bg-background rounded-xl border-2 border-secondary/60 text-secondary transition-all duration-200 text-left group hover:bg-accent hover:border-accent hover:text-accent-foreground hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30"
                    data-testid="vet-donation-btn"
                  >
                    <Stethoscope className="w-7 h-7 mb-3 transition-transform group-hover:scale-110" />
                    <p className="font-semibold text-base mb-1">{L(t("profile.vet.title", "Veteriner Desteği"))}</p>
                    <p className="text-sm opacity-80">{L(t("profile.vet.subtitle", "Sağlığına destek ol"))}</p>
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Genel Bağış Card */}
            <Card className="bg-card border border-border/50">
              <CardContent className="p-7 space-y-5">
                <h3 className="font-heading text-xl">{L(t("profile.donation.title", "Genel Bağış"))}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {L(t("profile.donation.description", "Tek seferlik destekle bakımına katkıda bulun. Kimsesiz canlara umut ol."))}
                </p>
                <Input
                  type="number"
                  value={donationAmount || ""}
                  onChange={(e) => setDonationAmount(Number(e.target.value) || 0)}
                  className="h-12 rounded-xl"
                  placeholder={L("Bağış tutarını sen belirle (TL)")}
                  min={100}
                  data-testid="donation-amount-input"
                />
                <p className="text-xs text-muted-foreground italic">
                  {L("Bağış tutarını sen belirlersin. Her katkı — büyüklüğü ne olursa olsun — bir cana dokunur.")}
                </p>
                <div className="pt-1">
                  <Button 
                    size="lg"
                    onClick={() => { setDonationFlowType("genel"); setShowDonationFlow(true); }}
                    disabled={!donationAmount || donationAmount < 100}
                    className="w-full btn-pill bg-primary text-primary-foreground shadow-md shadow-primary/20 font-semibold transition-all duration-200 hover:bg-primary hover:text-white hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="donate-btn"
                  >
                    <HandHeart className="w-5 h-5 mr-2" />
                    {donationAmount >= 100
                      ? `${donationAmount.toLocaleString(locale === "en" ? "en-US" : "tr-TR")} ${L("TL Bağış Yap")}`
                      : L("Bağış Yap")}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* DonationFlow Modal */}
            <DonationFlow
              open={showDonationFlow}
              onClose={() => setShowDonationFlow(false)}
              dogId={dogId}
              dogName={dog.name}
              type={donationFlowType}
              presetAmount={donationFlowType === "genel" && donationAmount >= 100 ? donationAmount : undefined}
              presetDonationType={donationFlowType === "genel" ? "general" : undefined}
            />
          </div>
        </div>
      </div>

      {/* Mobile sticky bottom CTA bar — only on mobile, hidden on lg+ */}
      {dog.status !== "sahiplendirildi" && (
        <div
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-md border-t border-border/50 shadow-2xl"
          data-testid="mobile-sticky-cta"
        >
          <div className="grid grid-cols-3 gap-1 p-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)]">
            <Link to={`/sahiplen/${dogId}`} className="flex flex-col items-center justify-center py-2 rounded-xl bg-primary text-primary-foreground active:scale-95 transition-transform">
              <Heart className="w-5 h-5 mb-0.5" strokeWidth={2} />
              <span className="text-[11px] font-semibold">{L("Sahiplen")}</span>
            </Link>
            {/* Koruyucu Aile her zaman katılabilir — public görünümde sayı gösterilmiyor */}
            <Link to={`/koruyucu-aile/${dogId}`} className="flex flex-col items-center justify-center py-2 rounded-xl border-2 border-secondary text-secondary active:scale-95 transition-transform">
              <Home className="w-5 h-5 mb-0.5" strokeWidth={2} />
              <span className="text-[11px] font-semibold">{L("Koruyucu")}</span>
            </Link>
            <button
              type="button"
              onClick={() => { setDonationFlowType("genel"); setShowDonationFlow(true); }}
              className="flex flex-col items-center justify-center py-2 rounded-xl bg-secondary text-secondary-foreground active:scale-95 transition-transform"
            >
              <HandHeart className="w-5 h-5 mb-0.5" strokeWidth={2} />
              <span className="text-[11px] font-semibold">{L("Bağış")}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
